import { Inject, Injectable } from "@angular/core";
import { AppType, APP_TYPE } from "../../../app/app.type";
import {
  getBomPartEntry,
  getBomPartQuantity,
  Part,
  SapItemCategory
} from "../../../../../shared/models/part";
import { DetailTableServiceType } from "./detail-table.service.type";
import { getManufacturerIdsByCPNs } from "../../api.service";
import { Customer } from "../../../../../shared/types/customers";

@Injectable()
export class DetailTableService implements DetailTableServiceType {
  docs: any[] = [];
  cleanDocs: any[] = [];
  selectedPartNumber = "";

  constructor(@Inject(APP_TYPE) private app: AppType) {}

  async getDocsByType(type: string) {
    this.docs = [];
    this.app.spinner.showSpinner();
    switch (type) {
      case "part":
        // The part documents provide the descriptive columns, but the parent
        // assembly owns the BOM-line values. Keep those sources separate and
        // merge the relation entry into every table row after fetching parts.
        const assemblyParts =
          this.app.part.cleanPart[this.app.fieldId.part.parts] || {};
        const cleanPartIds = Object.keys(assemblyParts);
        const fetchedParts = await this.app.part.getBomParts(assemblyParts);
        const fetchedPartsById = new Map(
          fetchedParts.map((part: Part) => [part._id || part.partNumber, part])
        );

        this.docs = cleanPartIds.map((id) => {
          const part = fetchedPartsById.get(id) || {
            _id: id,
            partNumber: id,
            description: this.app.text.part.noDataAvailable,
          };
          const entry = getBomPartEntry(assemblyParts[id]);

          return {
            ...part,
            _id: part._id || id,
            partNumber: part.partNumber || id,
            quantity: String(entry.quantity),
            designator: entry.designator || "",
            sapItemCategory: entry.sapItemCategory || "",
          };
        });

        this.cleanDocs = this.docs;
        this.app.part.currentAssemblies = this.cleanDocs;
        break;
      case "manufacturer":
        const partId = this.app.part.id;
        if (partId == null) {
          return;
        }
        this.docs = await this.app.manufacturer.getManufacturersByPartNumber(
          partId,
          true
        );
        this.cleanDocs = this.docs;
        break;
      default:
        this.docs = [];
    }
    this.app.spinner.hideSpinner();
  }

  getQuantity(doc: Part) {
    return getBomPartQuantity(this.getBomEntry(doc));
  }

  getBomEntry(doc: Part) {
    const partNumber = doc?._id || doc?.partNumber;
    return getBomPartEntry(
      this.app.part.cleanPart[this.app.fieldId.part.parts]?.[partNumber]
    );
  }

  updateBomEntry(
    doc: Part,
    field: "designator" | "sapItemCategory",
    value: string
  ) {
    const parts = this.app.part.part[this.app.fieldId.part.parts];
    if (parts == null || doc?._id == null) {
      return;
    }

    const entry = getBomPartEntry(parts[doc._id]);
    if (field === "sapItemCategory") {
      entry.sapItemCategory = (value || undefined) as SapItemCategory | undefined;
    } else {
      entry.designator = value || undefined;
    }
    parts[doc._id] = entry;
    doc[field] = value;
  }

  async deleteBOMItem(docType: string) {
    const items: any = this.app.table.selected;
    const docIds: string[] = [];
    items.forEach((item: any) => {
      docIds.push(item["_id"]);
    });

    if (this.app.leftNav.selectedBox == "part.billOfMaterialSection") {
      const editedPart: Part = this.app.part.cleanPart;
      docIds.forEach(
        (id: string) => delete editedPart[this.app.fieldId.part.parts][id]
      );
      await this.app.part.save(this.app.part.cleanPart, editedPart);
      this.app.successText = this.app.text.operations.deleteDocsDescription;
      setTimeout(() => {
        this.app.state.next({ hasSuccess: false });
      }, 6000);
    } else {
      await this.app.operations.triggerWork("delete-docs", docType, docIds);
      if (this.app.RM.mode === "not-used-assemblies") {
        await this.deleteManufacturersByPartIds(docIds);
      }
    }
    if (this.app.customers.expectCurrent !== Customer.MTU) {
      // build bom
      await this.app.operations.triggerWork("build-bom");
      // generate tree
      await this.app.tree.generateTree();
    }

    // TODO: after the documents have been deleted the table needs to be updated
    //   await this.getDocsByType("part");
    //   this.app.filterTable.resetAllFilters.next(true);
  }

  async deleteManufacturersByPartIds(partIds: string[]) {
    const manufacturerIds = await getManufacturerIdsByCPNs(
      this.app.customers.expectCurrent,
      partIds
    );

    if (manufacturerIds.length > 0) {
      await this.app.operations.triggerWork(
        "delete-docs",
        "manufacturer",
        manufacturerIds
      );
    }
  }
}
